import { useCallback, useState } from "react";

/**
 * Conmutador "Grafo | Tabla" del bloque de asignaciones, con la preferencia
 * recordada.
 *
 * La tabla **no es un resto del pasado que se pueda quitar**: es la alternativa
 * accesible del grafo (un canvas de nodos es lo peor con lector de pantalla) y
 * la salida cuando la ventana es estrecha. Que la eleccion se recuerde es parte
 * de eso: quien necesita la tabla la necesita SIEMPRE, y obligarle a conmutar
 * en cada visita convertiria la alternativa en un peaje.
 *
 * `localStorage` y no `sessionStorage` —al contrario que la pestana activa del
 * panel, que es navegacion de la sesion— justo por eso: es una preferencia de
 * accesibilidad de la persona, no de la visita.
 *
 * Modulo aparte del componente por `react-refresh/only-export-components`,
 * mismo criterio que `pestanasEstado.ts`.
 */
export const VISTAS = ["grafo", "tabla"] as const;

export type VistaAsignaciones = (typeof VISTAS)[number];

export const VISTA_STORAGE_KEY = "admin_modelos_vista";

const POR_DEFECTO: VistaAsignaciones = "grafo";

function esVista(valor: unknown): valor is VistaAsignaciones {
  return VISTAS.includes(valor as VistaAsignaciones);
}

export function useVistaAsignaciones(): [VistaAsignaciones, (v: VistaAsignaciones) => void] {
  const [vista, setVistaState] = useState<VistaAsignaciones>(() => {
    try {
      const guardada = localStorage.getItem(VISTA_STORAGE_KEY);
      // Se VALIDA: un valor escrito por otra version del panel (o a mano) no
      // puede dejar el bloque de asignaciones sin pintar ninguna vista.
      return esVista(guardada) ? guardada : POR_DEFECTO;
    } catch {
      return POR_DEFECTO;
    }
  });

  const setVista = useCallback((v: VistaAsignaciones) => {
    setVistaState(v);
    try {
      localStorage.setItem(VISTA_STORAGE_KEY, v);
    } catch {
      /* localStorage puede lanzar en modo privado; no puede tumbar el panel */
    }
  }, []);

  return [vista, setVista];
}
